// src/pages/mock-data-management-dashboard/components/ApiEndpointSimulator.jsx
import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const ApiEndpointSimulator = ({ mockData, onApiCall }) => {
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);
  const [showResponse, setShowResponse] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const getApiEndpoints = () => {
    return [
      // Users endpoints
      {
        id: 'get_users',
        method: 'GET',
        path: '/api/users',
        description: 'Retrieve all users',
        category: 'users',
        parameters: ['page', 'limit', 'role', 'status'],
        sampleResponse: mockData.users.slice(0, 3)
      },
      {
        id: 'get_user',
        method: 'GET',
        path: '/api/users/{id}',
        description: 'Retrieve a specific user',
        category: 'users',
        parameters: ['id'],
        sampleResponse: mockData.users[0] || {}
      },
      {
        id: 'create_user',
        method: 'POST',
        path: '/api/users',
        description: 'Create a new user',
        category: 'users',
        parameters: ['name', 'email', 'role'],
        sampleResponse: { id: 'user_new', message: 'User created successfully' }
      },
      
      // Biodata endpoints
      {
        id: 'get_biodata',
        method: 'GET',
        path: '/api/biodata',
        description: 'Retrieve all biodata records',
        category: 'biodataRecords',
        parameters: ['page', 'limit', 'status', 'skills'],
        sampleResponse: mockData.biodataRecords.slice(0, 3)
      },
      {
        id: 'get_biodata_by_id',
        method: 'GET',
        path: '/api/biodata/{id}',
        description: 'Retrieve specific biodata record',
        category: 'biodataRecords',
        parameters: ['id'],
        sampleResponse: mockData.biodataRecords[0] || {}
      },
      {
        id: 'update_biodata_status',
        method: 'PATCH',
        path: '/api/biodata/{id}/status',
        description: 'Update biodata status',
        category: 'biodataRecords',
        parameters: ['id', 'status', 'reviewNotes'],
        sampleResponse: { message: 'Status updated successfully' }
      },
      
      // Documents endpoints
      {
        id: 'get_documents',
        method: 'GET',
        path: '/api/documents',
        description: 'Retrieve all documents',
        category: 'documents',
        parameters: ['page', 'limit', 'type', 'status'],
        sampleResponse: mockData.documents.slice(0, 3)
      },
      {
        id: 'upload_document',
        method: 'POST',
        path: '/api/documents/upload',
        description: 'Upload a new document',
        category: 'documents',
        parameters: ['file', 'type', 'biodataId'],
        sampleResponse: { id: 'doc_new', message: 'Document uploaded successfully' }
      },
      
      // System logs endpoints
      {
        id: 'get_logs',
        method: 'GET',
        path: '/api/logs',
        description: 'Retrieve system logs',
        category: 'systemLogs',
        parameters: ['page', 'limit', 'level', 'startDate', 'endDate'],
        sampleResponse: mockData.systemLogs.slice(0, 5)
      }
    ];
  };

  const getMethodColor = (method) => {
    const colorMap = {
      GET: 'text-success-600 bg-success-50 border-success-200',
      POST: 'text-accent-600 bg-accent-50 border-accent-200',
      PUT: 'text-warning-600 bg-warning-50 border-warning-200',
      PATCH: 'text-warning-600 bg-warning-50 border-warning-200',
      DELETE: 'text-error-600 bg-error-50 border-error-200'
    };
    return colorMap[method] || 'text-secondary-600 bg-secondary-50 border-secondary-200';
  };

  const getStatusColor = (isActive) => {
    return isActive 
      ? 'text-success-600 bg-success-50' :'text-secondary-600 bg-secondary-50';
  };

  const simulateApiCall = async (endpoint) => {
    setIsLoading(true);
    setSelectedEndpoint(endpoint);
    
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Generate response based on endpoint
      let response = {
        status: 200,
        statusText: 'OK',
        data: endpoint.sampleResponse,
        headers: {
          'Content-Type': 'application/json',
          'X-Total-Count': Array.isArray(endpoint.sampleResponse) ? endpoint.sampleResponse.length : 1
        },
        timestamp: new Date().toISOString()
      };
      
      // Add some variation for different methods
      if (endpoint.method === 'POST') {
        response.status = 201;
        response.statusText = 'Created';
      } else if (endpoint.method === 'PATCH' || endpoint.method === 'PUT') {
        response.status = 200;
        response.statusText = 'OK';
      }
      
      setResponseData(response);
      setShowResponse(true);
      onApiCall?.();
      
    } catch (error) {
      setResponseData({
        status: 500,
        statusText: 'Internal Server Error',
        error: 'Simulation error',
        timestamp: new Date().toISOString()
      });
      setShowResponse(true);
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // You could show a toast notification here
    });
  };

  const endpoints = getApiEndpoints();
  const categories = [...new Set(endpoints.map(e => e.category))];

  return (
    <div className="card">
      <div className="p-6 border-b border-secondary-200">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-accent-100 rounded-lg flex items-center justify-center">
            <Icon name="Zap" size={20} className="text-accent-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-text-primary">API Endpoint Simulator</h3>
            <p className="text-sm text-text-secondary">
              Test API endpoints with live mock data responses
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 p-6">
        {/* Endpoints List */}
        <div>
          <h4 className="font-medium text-text-primary mb-4">Available Endpoints</h4>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {endpoints.map((endpoint) => {
              const hasData = mockData[endpoint.category] && mockData[endpoint.category].length > 0;
              return (
                <div
                  key={endpoint.id}
                  className={`p-4 rounded-lg border transition-all cursor-pointer ${
                    selectedEndpoint?.id === endpoint.id
                      ? 'border-primary bg-primary-50' :'border-secondary-200 hover:border-secondary-300 hover:bg-secondary-50'
                  }`}
                  onClick={() => simulateApiCall(endpoint)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded border ${
                        getMethodColor(endpoint.method)
                      }`}>
                        {endpoint.method}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        getStatusColor(hasData)
                      }`}>
                        {hasData ? 'Active' : 'No Data'}
                      </span>
                    </div>
                    {isLoading && selectedEndpoint?.id === endpoint.id && (
                      <Icon name="Loader2" size={16} className="animate-spin text-primary" />
                    )}
                  </div>
                  <div className="font-mono text-sm text-text-primary mb-1">
                    {endpoint.path}
                  </div>
                  <div className="text-xs text-text-secondary mb-2">
                    {endpoint.description}
                  </div>
                  {endpoint.parameters.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {endpoint.parameters.map((param) => (
                        <span key={param} className="px-2 py-1 text-xs bg-secondary-100 text-secondary-600 rounded">
                          {param}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Response Panel */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-text-primary">Response</h4>
            {responseData && (
              <button
                onClick={() => copyToClipboard(JSON.stringify(responseData, null, 2))}
                className="btn btn-secondary btn-sm flex items-center space-x-1"
              >
                <Icon name="Copy" size={14} />
                <span>Copy</span>
              </button>
            )}
          </div>
          
          <div className="bg-secondary-900 rounded-lg p-4 font-mono text-sm text-secondary-100 h-96 overflow-auto">
            {!showResponse && (
              <div className="flex items-center justify-center h-full text-secondary-400">
                <div className="text-center">
                  <Icon name="MousePointer" size={48} className="mx-auto mb-4 opacity-50" />
                  <p>Click on an endpoint to simulate API call</p>
                </div>
              </div>
            )}
            
            {isLoading && (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <Icon name="Loader2" size={32} className="animate-spin mx-auto mb-4 text-accent-400" />
                  <p className="text-secondary-300">Simulating API call...</p>
                </div>
              </div>
            )}
            
            {showResponse && responseData && !isLoading && (
              <div>
                {/* Response Headers */}
                <div className="mb-4 pb-4 border-b border-secondary-700">
                  <div className="text-accent-400 mb-2">HTTP/1.1 {responseData.status} {responseData.statusText}</div>
                  {responseData.headers && Object.entries(responseData.headers).map(([key, value]) => (
                    <div key={key} className="text-secondary-300">
                      <span className="text-secondary-400">{key}:</span> {value}
                    </div>
                  ))}
                  <div className="text-secondary-300">
                    <span className="text-secondary-400">Timestamp:</span> {responseData.timestamp}
                  </div>
                </div>
                
                {/* Response Body */}
                <div>
                  <div className="text-accent-400 mb-2">Response Body:</div>
                  <pre className="text-secondary-100 whitespace-pre-wrap">
                    {JSON.stringify(responseData.data || responseData.error, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiEndpointSimulator;